import React, { useState, useEffect } from 'react';
import QuizEngine from './components/QuizEngine';
import AdminDashboard from './components/AdminDashboard';
import AdminLogin from './components/AdminLogin';
import UserLogin from './components/UserLogin';
import ForgotPassword from './components/ForgotPassword';
// import GlobalThemeToggle from './components/GlobalThemeToggle';
// import { ThemeProvider } from './contexts/ThemeContext';
import AuthServiceV2 from './services/authServiceV2';
import { AUTH_ROLES, ROUTE_NAMESPACES } from './utils/authConstants';
import sciLogo from './assets/sci_logo.png';
import './App.css';

function AppV2() {
  const [currentView, setCurrentView] = useState('loading');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initialize app based on current path and authentication
  useEffect(() => {
    const initializeApp = () => {
      const path = window.location.pathname;
      let requiredRole = null;
      let loginRoute = '/login';

      // Determine required role based on path
      if (path.startsWith(ROUTE_NAMESPACES.ADMIN)) {
        requiredRole = AUTH_ROLES.ADMIN;
        loginRoute = '/admin/login';
      } else if (path.startsWith(ROUTE_NAMESPACES.REVIEWER)) {
        requiredRole = AUTH_ROLES.REVIEWER;
        loginRoute = '/reviewer/login';
      } else {
        requiredRole = AUTH_ROLES.USER;
        loginRoute = '/login';
      }

      // Check if user is authenticated for the required role
      if (AuthServiceV2.isAuthenticated(requiredRole)) {
        const userData = AuthServiceV2.getCurrentUser(requiredRole);
        if (userData) {
          setUser(userData);
          
          // Set appropriate view based on role
          if (requiredRole === AUTH_ROLES.ADMIN) {
            setCurrentView('admin');
          } else if (requiredRole === AUTH_ROLES.REVIEWER) {
            setCurrentView('reviewer');
          } else {
            setCurrentView('quiz');
          }
          setLoading(false);
          return;
        }
      }

      // For user routes (/), don't redirect - just show login page
      // For admin/reviewer routes, redirect to their respective login pages
      if (requiredRole !== AUTH_ROLES.USER && path !== loginRoute && !path.includes('/login')) {
        window.location.href = loginRoute;
        return;
      }

      // Show login page
      if (requiredRole === AUTH_ROLES.ADMIN) {
        setCurrentView('adminLogin');
      } else if (requiredRole === AUTH_ROLES.REVIEWER) {
        setCurrentView('reviewerLogin');
      } else {
        setCurrentView('login');
      }
      setLoading(false);
    };

    initializeApp();
  }, []);

  // Handle route changes (browser back/forward)
  useEffect(() => {
    const handlePopState = () => {
      window.location.reload(); // Simple approach to re-initialize on route change
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleLogin = (userData, role) => {
    setUser(userData);
    
    // Set view based on role
    if (role === AUTH_ROLES.ADMIN) {
      setCurrentView('admin');
      window.history.pushState({}, '', '/admin');
    } else if (role === AUTH_ROLES.REVIEWER) {
      setCurrentView('reviewer');
      window.history.pushState({}, '', '/reviewer');
    } else {
      setCurrentView('quiz');
      window.history.pushState({}, '', '/');
    }
  };

  const handleLogout = async (role) => {
    await AuthServiceV2.logout(role);
    setUser(null);
    
    // Redirect to appropriate login
    if (role === AUTH_ROLES.ADMIN) {
      setCurrentView('adminLogin');
      window.history.pushState({}, '', '/admin/login');
    } else if (role === AUTH_ROLES.REVIEWER) {
      setCurrentView('reviewerLogin');
      window.history.pushState({}, '', '/reviewer/login');
    } else {
      setCurrentView('login');
      window.history.pushState({}, '', '/login');
    }
  };

  const renderView = () => {
    if (loading) {
      return <div className="loading">Loading...</div>;
    }

    switch (currentView) {
      case 'admin':
        return (
          <AdminDashboard 
            onLogout={() => handleLogout(AUTH_ROLES.ADMIN)} 
          />
        );
      case 'reviewer':
        return (
          <div className="reviewer-dashboard">
            <h2>Reviewer Dashboard</h2>
            <p>Reviewer functionality coming soon...</p>
            <button onClick={() => handleLogout(AUTH_ROLES.REVIEWER)}>
              Logout
            </button>
          </div>
        );
      case 'quiz':
        return (
          <QuizEngine 
            currentUser={user} 
            onLogout={() => handleLogout(AUTH_ROLES.USER)} 
          />
        );
      case 'adminLogin':
        return (
          <AdminLogin 
            onLogin={(userData, role) => handleLogin(userData, role)} 
          />
        );
      case 'reviewerLogin':
        return (
          <div className="login-container">
            <h2>Reviewer Login</h2>
            <p>Reviewer login coming soon...</p>
            <button onClick={() => window.location.href = '/login'}>
              Back to User Login
            </button>
          </div>
        );
      case 'forgotPassword':
        return (
          <ForgotPassword 
            onBackToLogin={() => {
              setCurrentView('login');
              window.history.pushState({}, '', '/login');
            }} 
          />
        );
      case 'login':
      default:
        return (
          <UserLogin 
            onLogin={(userData, role) => handleLogin(userData, role)} 
            onForgotPassword={() => {
              setCurrentView('forgotPassword');
              window.history.pushState({}, '', '/forgot-password');
            }} 
          />
        );
    }
  };

  return (
    <div className="App">
        {/* Navigation - only show for logged-in users, but hide during quiz */}
        {user && currentView !== 'quiz' && (
          <nav className="app-nav">
            <div className="nav-container">
              <div className="nav-brand">
                <img src={sciLogo} alt="SCI Logo" className="sci-logo" />
                {/* <span className="role-badge">{user.role}</span> */}
              </div>
              <div className="nav-links">
                {user.role === AUTH_ROLES.USER && (
                  <button 
                    className={`nav-link ${currentView === 'quiz' ? 'active' : ''}`}
                    onClick={() => {
                      setCurrentView('quiz');
                      window.history.pushState({}, '', '/');
                    }}
                  >
                    Quiz
                  </button>
                )}
                <button 
                  className="nav-link logout-btn"
                  onClick={() => handleLogout(user.role)}
                >
                  Logout
                </button>
              </div>
            </div>
          </nav>
        )}

        {/* Main Content */}
        <main className="app-main">
          {renderView()}
        </main>
        
        {/* Global Theme Toggle */}
        {/* <GlobalThemeToggle /> */}
    </div>
  );
}

export default AppV2;
